import memoize from 'lodash/memoize.js';
import { toMajorUnit } from './conversions.js';
/**
 *
 * @description A memoized function to get a currency formatter. Constructing this object is slow, memoizing it is a 10x speedup
 *
 * @param bcpTag BCP-47 tag (e.g. en-US)
 * @param currency currency (e.g. USD)
 * @returns A currency formatter
 */
const getCurrencyFormatter = memoize((bcpTag, currency, currencyDisplay) => new Intl.NumberFormat(bcpTag, {
    currency,
    currencyDisplay,
    style: 'currency',
}), 
// cache key -- because it's a hot path, use quicker string concat than typical JSON.stringify
(bcpTag, currency, currencyDisplay) => bcpTag + '#' + currency + '#' + currencyDisplay);
/**
 * Formats a major unit amount to a currency string (e.g. 1.25 => "$1.25")
 *
 * @param majorAmount major unit amount (e.g. dollars)
 * @param options options object
 * @param options.bcpTag
 * @param options.currency
 * @param options.currencyDisplay
 * @param options.includeSymbol
 * @returns The currency in a localized presentation.
 */
export const formatMajorAmount = (majorAmount, { bcpTag = 'en-US', currency = 'USD', currencyDisplay = 'symbol', includeSymbol = true, } = {}) => {
    const formatter = getCurrencyFormatter(bcpTag, currency, currencyDisplay);
    return includeSymbol
        ? formatter.format(majorAmount)
        : formatter
            .formatToParts(majorAmount)
            .reduce((acc, { type, value }) => (type === 'currency' ? acc : acc + value), '');
};
/**
 * Formats a minor unit amount as a major unit currency string (e.g. 125 => "$1.25")
 *
 * @param minorAmount minor unit amount (e.g. cents)
 * @param options options object
 * @returns The currency in a localized presentation.
 */
export const formatMinorAmount = (minorAmount, options) => formatMajorAmount(toMajorUnit(minorAmount), options);
/**
 * Converts an amount from cents to dollars as compact as possible. If the amount is 700 it will be converted to $7. If the amount is 712 it will be converted to $7.12.
 *
 * @param minorAmount minor unit amount (e.g. cents)
 * @param options options object
 * @returns The currency in a localized presentation.
 */
export const formatAmountCompact = (minorAmount, options) => {
    const amount = formatMinorAmount(minorAmount, options);
    if (amount.endsWith('.00')) {
        return amount.slice(0, -3);
    }
    return amount;
};
export const formatPriceDifference = (amount, options) => {
    return `${options?.includeSign ? (amount > 0 ? '+' : '-') : ''}${formatMinorAmount(Math.abs(amount), options)}`;
};
//# sourceMappingURL=format.js.map